/**
 * BAB "Comments" tab — every comment in one flat table, filterable and sortable.
 *
 * The side panel only ever shows the comments of the one anchor it was opened from. This is
 * the view that answers the cross-cutting question: what has been said about this company,
 * this line, this month, by whom.
 *
 * Split of duties, matching "My Queries": FILTERING is server-side (it decides which rows
 * exist and enforces the level-3 booking scope in SQL), SORTING is client-side (the set is
 * bounded, so re-sorting must be instant).
 *
 * Every row is clickable and jumps to the place the comment hangs on, through the same
 * `navigateToComment` path the panel's cards use.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageSquare, ArrowUpRight, RefreshCw, AlertTriangle } from 'lucide-react';
import { babReportApi } from '../../services/babReportApi';
import { useTableSort } from '../../hooks/useTableSort';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { ColumnResizeHandle } from '../ui/ColumnResizeHandle';
import { describeTransactionRef } from '../../utils/babTransactionRef';
import { MONTH_NAMES } from '../../utils/babFormat';
import { formatCompanyLabel } from '../../utils/buTheme';
import type {
    BabCommentInboxItem, BabCommentInboxFilters, BabCommentLevel, BabPeriod,
} from '../../types/babReport';

const LEVEL_CHIP: Record<BabCommentLevel, string> = {
    line: 'bg-gray-100 text-gray-600',
    account: 'bg-indigo-100 text-indigo-700',
    booking: 'bg-amber-100 text-amber-800',
};

type SortKey =
    | 'refPeriod' | 'level' | 'line' | 'company' | 'bu' | 'account'
    | 'anchor' | 'comment' | 'author' | 'created' | 'modified';

const COLUMNS: { key: SortKey; label: string; width: number }[] = [
    { key: 'refPeriod', label: 'Period', width: 100 },
    { key: 'level', label: 'Level', width: 90 },
    { key: 'line', label: 'Line', width: 240 },
    { key: 'company', label: 'Company', width: 100 },
    { key: 'bu', label: 'BU', width: 70 },
    { key: 'account', label: 'Account', width: 220 },
    { key: 'anchor', label: 'Booking', width: 300 },
    { key: 'comment', label: 'Comment', width: 420 },
    { key: 'author', label: 'Author', width: 200 },
    { key: 'created', label: 'Created', width: 110 },
    { key: 'modified', label: 'Modified', width: 110 },
];

function periodLabel(item: { refPeriodYear: number; refPeriodMonth: number }): string {
    return `${MONTH_NAMES[item.refPeriodMonth - 1] ?? item.refPeriodMonth} ${item.refPeriodYear}`;
}

function formatDate(value: string | null): string {
    if (!value) return '';
    return new Date(value).toLocaleDateString('de-CH', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/**
 * Sort accessors. `refPeriod` deliberately sorts on the arithmetic YYYYMM key rather than the
 * label — "Feb 2026" before "Jan 2026" alphabetically is not what anybody means by sorting a
 * period column.
 */
const ACCESSORS: Record<SortKey, (i: BabCommentInboxItem) => string | number | null> = {
    refPeriod: i => i.refPeriodYear * 100 + i.refPeriodMonth,
    level: i => i.level,
    line: i => `${i.lineCode ?? ''} ${i.lineName ?? ''}`.trim(),
    company: i => i.sourceCompanyCode ?? i.babCompanyCode ?? '',
    bu: i => i.buCode ?? '',
    account: i => i.accountCode ?? '',
    anchor: i => describeTransactionRef(i.transactionRef) ?? '',
    comment: i => i.commentText,
    author: i => i.createdBy ?? '',
    created: i => i.createdDate ?? '',
    modified: i => i.modifiedDate ?? '',
};

interface Props {
    /** The frozen periods, newest first — drives the two period pickers. */
    periods: BabPeriod[];
    /** Jump to the place a comment hangs on. Switches period and reopens the drill-down. */
    onOpen: (item: BabCommentInboxItem) => void;
}

export function BabCommentInboxTab({ periods, onOpen }: Props) {
    const [filters, setFilters] = useState<BabCommentInboxFilters>({});
    /** Applied on submit, not on every keystroke — the text boxes would refetch per letter. */
    const [applied, setApplied] = useState<BabCommentInboxFilters>({});
    const [items, setItems] = useState<BabCommentInboxItem[]>([]);
    const [truncated, setTruncated] = useState(false);
    const [limit, setLimit] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'bab-comments-col-widths');
    const { sorted, sort, toggleSort } = useTableSort(items, ACCESSORS, { key: 'refPeriod', dir: 'desc' });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await babReportApi.getCommentInbox(applied);
            setItems(res.items);
            setTruncated(res.truncated);
            setLimit(res.limit);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load comments');
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [applied]);

    useEffect(() => { void load(); }, [load]);

    /** The two period pickers offer exactly the frozen periods, oldest last. */
    const periodOptions = useMemo(
        () => periods.map(p => ({ value: `${p.periodYear}-${p.periodMonth}`, label: p.label })),
        [periods]
    );

    const companyOptions = useMemo(() => {
        const set = new Set<string>();
        for (const i of items) if (i.sourceCompanyCode) set.add(i.sourceCompanyCode);
        return Array.from(set).sort();
    }, [items]);

    const patch = (u: Partial<BabCommentInboxFilters>) => setFilters(f => ({ ...f, ...u }));

    const setPeriod = (which: 'from' | 'to', raw: string) => {
        if (!raw) {
            patch(which === 'from' ? { fromYear: undefined, fromMonth: undefined } : { toYear: undefined, toMonth: undefined });
            return;
        }
        const [y, m] = raw.split('-').map(Number);
        patch(which === 'from' ? { fromYear: y, fromMonth: m } : { toYear: y, toMonth: m });
    };

    const apply = () => setApplied(filters);
    const reset = () => { setFilters({}); setApplied({}); };

    const selectClass = 'border rounded-md px-2 py-1.5 text-sm bg-white';
    const inputClass = 'border rounded-md px-2 py-1.5 text-sm';
    const labelClass = 'text-xs font-medium text-gray-500 block mb-1';
    const th = 'sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left text-xs font-medium text-gray-500 select-none relative cursor-pointer hover:bg-gray-100';
    const cell = 'px-3 py-2 truncate';

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <label className={labelClass}>Period from</label>
                        <select
                            className={selectClass}
                            value={filters.fromYear ? `${filters.fromYear}-${filters.fromMonth}` : ''}
                            onChange={e => setPeriod('from', e.target.value)}
                        >
                            <option value="">Earliest</option>
                            {periodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Period to</label>
                        <select
                            className={selectClass}
                            value={filters.toYear ? `${filters.toYear}-${filters.toMonth}` : ''}
                            onChange={e => setPeriod('to', e.target.value)}
                        >
                            <option value="">Latest</option>
                            {periodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Level</label>
                        <select
                            className={selectClass}
                            value={filters.level ?? ''}
                            onChange={e => patch({ level: e.target.value as BabCommentLevel | '' })}
                        >
                            <option value="">All levels</option>
                            <option value="line">Line</option>
                            <option value="account">Account</option>
                            <option value="booking">Booking</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Source company</label>
                        <select
                            className={selectClass}
                            value={filters.sourceCompany ?? ''}
                            onChange={e => patch({ sourceCompany: e.target.value })}
                        >
                            <option value="">All companies</option>
                            {companyOptions.map(c => (
                                <option key={c} value={c}>{formatCompanyLabel(c)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>Line code</label>
                        <input
                            className={`${inputClass} w-28`}
                            value={filters.lineCode ?? ''}
                            onChange={e => patch({ lineCode: e.target.value })}
                            onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                            placeholder="11900"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Account</label>
                        <input
                            className={`${inputClass} w-32`}
                            value={filters.accountCode ?? ''}
                            onChange={e => patch({ accountCode: e.target.value })}
                            onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                            placeholder="52000"
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Author</label>
                        <input
                            className={`${inputClass} w-44`}
                            value={filters.author ?? ''}
                            onChange={e => patch({ author: e.target.value })}
                            onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                            disabled={!!filters.mine}
                            placeholder="name or e-mail"
                        />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <label className={labelClass}>Text</label>
                        <input
                            className={`${inputClass} w-full`}
                            value={filters.search ?? ''}
                            onChange={e => patch({ search: e.target.value })}
                            onKeyDown={e => { if (e.key === 'Enter') apply(); }}
                            placeholder="search the comment and the booking"
                        />
                    </div>
                    <label className="inline-flex items-center gap-1.5 text-sm pb-1.5">
                        <input
                            type="checkbox"
                            checked={!!filters.mine}
                            onChange={e => patch({ mine: e.target.checked })}
                        />
                        Only mine
                    </label>
                    <div className="flex items-center gap-2 pb-0.5">
                        <button onClick={apply} className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700">
                            Apply
                        </button>
                        <button onClick={reset} className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50">
                            Reset
                        </button>
                        <button onClick={() => void load()} className="p-1.5 border rounded-md hover:bg-gray-50" title="Reload">
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            {truncated && (
                <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    More than {limit} comments match. Only the {limit} most recent are listed — narrow the filter to see the rest.
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {loading ? (
                    <p className="p-6 text-sm text-gray-500">Loading…</p>
                ) : sorted.length === 0 ? (
                    <div className="p-10 text-center text-gray-500">
                        <MessageSquare className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No comments match this filter.</p>
                    </div>
                ) : (
                    <div className="overflow-auto max-h-[calc(100vh-360px)]">
                        <table
                            className="w-full table-fixed text-sm"
                            style={{ minWidth: totalWidthOf(COLUMNS.map(c => c.key)) + 60 }}
                        >
                            <colgroup>
                                {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                                <col style={{ width: 60 }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    {COLUMNS.map(c => (
                                        <th
                                            key={c.key}
                                            className={th}
                                            onClick={() => { if (!shouldSuppressClick()) toggleSort(c.key); }}
                                            title={`${c.label} — click to sort, drag the edge to resize`}
                                        >
                                            <span className="block truncate">
                                                {c.label}
                                                {sort.key === c.key && (
                                                    <span className="ml-1 text-gray-900">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                                                )}
                                            </span>
                                            <ColumnResizeHandle
                                                label={c.label}
                                                active={resizingKey === c.key}
                                                onStart={x => startResize(c.key, x)}
                                                onReset={() => resetWidth(c.key)}
                                            />
                                        </th>
                                    ))}
                                    <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map(c => (
                                    <tr key={c.commentId} className="hover:bg-gray-50 align-top">
                                        <td className={`${cell} whitespace-nowrap`}>{periodLabel(c)}</td>
                                        <td className={cell}>
                                            <span className={`text-[11px] font-medium rounded px-1.5 py-0.5 uppercase ${LEVEL_CHIP[c.level]}`}>
                                                {c.level}
                                            </span>
                                        </td>
                                        <td className={cell} title={`${c.lineCode} ${c.lineName ?? ''}`}>
                                            <span className="text-gray-500">{c.lineCode}</span>{' '}
                                            {c.lineName}
                                        </td>
                                        <td className={`${cell} uppercase`} title={c.sourceCompanyCode ? formatCompanyLabel(c.sourceCompanyCode) : undefined}>
                                            {c.sourceCompanyCode ?? c.babCompanyCode ?? '—'}
                                        </td>
                                        <td className={cell}>{c.buCode ?? '—'}</td>
                                        <td className={cell} title={c.accountName ? `${c.accountCode} — ${c.accountName}` : c.accountCode ?? ''}>
                                            {c.accountCode
                                                ? <>{c.accountCode}{c.accountName ? ` — ${c.accountName}` : ''}</>
                                                : '—'}
                                        </td>
                                        <td className={cell} title={describeTransactionRef(c.transactionRef) ?? undefined}>
                                            {describeTransactionRef(c.transactionRef) ?? '—'}
                                        </td>
                                        <td className={cell} title={c.commentText}>{c.commentText}</td>
                                        <td className={cell} title={c.createdBy ?? ''}>{c.createdBy}</td>
                                        <td className={`${cell} whitespace-nowrap`}>{formatDate(c.createdDate)}</td>
                                        <td className={`${cell} whitespace-nowrap`} title={c.modifiedBy ?? ''}>
                                            {formatDate(c.modifiedDate)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <button
                                                onClick={() => onOpen(c)}
                                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                                                title="Open the place this comment belongs to"
                                            >
                                                Open <ArrowUpRight className="h-3 w-3" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <p className="text-xs text-gray-400">
                {sorted.length} comment{sorted.length === 1 ? '' : 's'}. Bookings you have no detail access to are
                not listed. Comments written in Vendor Cost Analysis are not shown — they carry no BAB line.
            </p>
        </div>
    );
}
